<template>
  <v-container fluid class="pa-0">
    <v-empty-state
      icon="mdi-calendar"
      class="w-100 align-center justify-center"
      v-if="!hasData && !loading"
    >
      <template v-slot:headline>
        <div class="text-h4">No sales data found</div>
      </template>
    </v-empty-state>

    <v-row>
      <v-col cols="12">
        <!-- <v-card> -->
        <!--   <v-card-text> -->
        <v-alert v-if="error" type="error" class="mb-4 mx-4">
          {{ error }}
        </v-alert>

        <v-alert v-if="!matrixData && !loading" type="info" class="mb-4 mx-4">
          No sales data available. Start by submitting sales records.
        </v-alert>

        <div v-if="loading" class="text-center">
          <v-progress-circular indeterminate></v-progress-circular>
        </div>

        <div v-else-if="matrixData && hasData">
          <v-alert v-if="exporting" type="info" density="compact" class="mb-4 mx-4">
            <v-progress-linear indeterminate class="mt-2"></v-progress-linear>
            <div class="text-caption mt-1">Exporting to CSV...</div>
          </v-alert>

          <v-table density="comfortable" class="sticky-table">
            <thead>
              <tr>
                <th rowspan="2">
                  <v-btn
                    icon="mdi-arrow-expand-vertical"
                    @click="expandAll"
                    :disabled="!hasData"
                    variant="plain"
                  ></v-btn>
                  <v-btn
                    icon="mdi-arrow-collapse-vertical"
                    @click="collapseAll"
                    :disabled="!hasData"
                    variant="plain"
                  ></v-btn>
                </th>
                <th v-for="month in matrixData.months" :key="month" colspan="1" class="text-center">
                  {{ formatMonth(month) }}
                </th>
                <th rowspan="2" class="sticky-header text-center">Total</th>
              </tr>
            </thead>
            <tbody>
              <template v-for="city in getCitiesWithData()" :key="city">
                <!-- City Header Row -->
                <tr class="city-header">
                  <td class="sticky-cell city-name pl-0" @click="toggleCity(city)">
                    <v-icon small class="mr-1">
                      {{ isCityExpanded(city) ? "mdi-chevron-down" : "mdi-chevron-right" }}
                    </v-icon>
                    <strong :class="cityColor(city)">{{ city }}</strong>
                  </td>

                  <td
                    v-for="month in safeArray(matrixData.months)"
                    :key="month"
                    class="text-right city-month-total"
                    :class="cityColor(city)"
                  >
                    <strong>{{
                      formatAmount(getSafeValue(matrixData.rowTotals, [city, month], 0))
                    }}</strong>
                  </td>
                  <td class="text-right grand-city-total">
                    <strong>{{ formatAmount(getCityTotal(city)) }}</strong>
                  </td>
                </tr>

                <!-- Locations for this city -->
                <template v-if="isCityExpanded(city)">
                  <template v-for="location in getLocationsWithData(city)" :key="location">
                    <!-- Location Total Row -->
                    <tr>
                      <td style="padding-left: 1rem" @click="toggleLocation(city, location)">
                        <v-icon small class="mr-1">
                          {{
                            isLocationExpanded(city, location)
                              ? "mdi-chevron-down"
                              : "mdi-chevron-right"
                          }}
                        </v-icon>
                        <span :class="locationColor(location)">{{ location }}</span>
                      </td>
                      <td
                        v-for="month in safeArray(matrixData.months)"
                        :key="month"
                        class="text-right location-month-total"
                        :class="locationColor(location)"
                      >
                        {{ formatAmount(getLocationCityMonthTotal(city, location, month)) }}
                      </td>
                      <td class="text-right text-disabled">
                        <strong>{{ formatAmount(getLocationCityTotal(city, location)) }}</strong>
                      </td>
                    </tr>

                    <!-- Brands for this location -->
                    <template v-if="isLocationExpanded(city, location)">
                      <template v-for="brand in getBrandsWithData(city, location)" :key="brand">
                        <!-- Brand Total Row -->
                        <tr>
                          <td
                            style="padding-left: 2rem"
                            @click="toggleBrand(city, location, brand)"
                          >
                            <v-icon small class="mr-1">
                              {{
                                isBrandExpanded(city, location, brand)
                                  ? "mdi-chevron-down"
                                  : "mdi-chevron-right"
                              }}
                            </v-icon>
                            <span :class="brandColor(brand)">{{ brand }}</span>
                          </td>
                          <td
                            v-for="month in safeArray(matrixData.months)"
                            :key="month"
                            class="text-right"
                            :class="brandColor(brand)"
                          >
                            {{
                              formatAmount(
                                getBrandLocationCityMonthTotal(city, location, brand, month),
                              )
                            }}
                          </td>
                          <td class="text-right text-disabled">
                            <strong>{{
                              formatAmount(getBrandLocationCityTotal(city, location, brand))
                            }}</strong>
                          </td>
                        </tr>

                        <!-- Platforms for this brand -->
                        <template v-if="isBrandExpanded(city, location, brand)">
                          <tr
                            v-for="platform in getPlatformsWithData(city, location, brand)"
                            :key="platform"
                            class="platform-row"
                          >
                            <td>
                              <span class="platform-indent">{{ platform }}</span>
                            </td>
                            <td
                              v-for="month in safeArray(matrixData.months)"
                              :key="month"
                              class="text-right platform-amount"
                            >
                              {{
                                formatAmount(
                                  getPlatformData(city, location, brand, platform, month),
                                )
                              }}
                            </td>
                            <td class="text-right">
                              {{
                                formatAmount(
                                  getPlatformTotalForBrand(city, location, brand, platform),
                                )
                              }}
                            </td>
                          </tr>
                        </template>
                      </template>
                    </template>
                  </template>
                </template>
              </template>
            </tbody>

            <!-- Grand Totals Footer -->
            <tfoot>
              <tr class="grand-totals-row">
                <td class="sticky-cell"><strong>Total</strong></td>
                <td
                  v-for="month in safeArray(matrixData.months)"
                  :key="month"
                  class="text-right grand-month-total"
                >
                  <strong>{{ formatAmount(getGrandMonthTotal(month)) }}</strong>
                </td>
                <td class="text-right overall-total">
                  <strong>{{ formatAmount(getSafeValue(matrixData, ["grandTotal"], 0)) }}</strong>
                </td>
              </tr>
            </tfoot>
          </v-table>

          <!-- Summary Section -->
          <!-- <v-row class="mt-6" v-if="hasSummaryData"> -->
          <!--   <v-col cols="12" md="6"> -->
          <!--     <v-card class="summary-card" variant="outlined"> -->
          <!--       <v-card-title class="text-h6">Platform Performance</v-card-title> -->
          <!--       <v-card-text> -->
          <!--         <v-row> -->
          <!--           <v-col -->
          <!--             v-for="platform in getPlatformsWithAnyData()" -->
          <!--             :key="platform" -->
          <!--             cols="6" -->
          <!--           > -->
          <!--             <div class="platform-summary"> -->
          <!--               <div class="text-body-2 text-medium-emphasis">{{ platform }}</div> -->
          <!--               <div class="text-h6 text-primary font-weight-bold"> -->
          <!--                 {{ formatAmount(getPlatformTotal(platform)) }} -->
          <!--               </div> -->
          <!--             </div> -->
          <!--           </v-col> -->
          <!--         </v-row> -->
          <!--       </v-card-text> -->
          <!--     </v-card> -->
          <!--   </v-col> -->
          <!--   <v-col cols="12" md="6"> -->
          <!--     <v-card class="summary-card" variant="outlined"> -->
          <!--       <v-card-title class="text-h6">Location Performance</v-card-title> -->
          <!--       <v-card-text> -->
          <!--         <v-row> -->
          <!--           <v-col -->
          <!--             v-for="location in getLocationsWithAnyData()" -->
          <!--             :key="location" -->
          <!--             cols="6" -->
          <!--           > -->
          <!--             <div class="location-summary"> -->
          <!--               <div class="text-body-2 text-medium-emphasis">{{ location }}</div> -->
          <!--               <div class="text-h6 text-primary font-weight-bold"> -->
          <!--                 {{ formatAmount(getLocationTotal(location)) }} -->
          <!--               </div> -->
          <!--             </div> -->
          <!--           </v-col> -->
          <!--         </v-row> -->
          <!--       </v-card-text> -->
          <!--     </v-card> -->
          <!--   </v-col> -->
          <!-- </v-row> -->

          <!-- NET sales note -->
          <div class="text-caption text-disabled text-right mt-2 mr-4">
            Sales are NET from January 2026 onwards
          </div>
        </div>
        <!-- </v-card-text> -->
        <!-- </v-card> -->
      </v-col>
    </v-row>

  </v-container>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, reactive, computed, watchEffect } from "vue";
import { useRouter } from "vue-router";
import api from "@/utils/api.js";
import { useSalesForm } from "@/composables/useSalesForm.js";
import { useConfirmDialog } from "@/composables/useConfirmDialog.js";
import { useAppBarActions } from "@/composables/useAppBarActions.js";

const router = useRouter();
const { cityColor, locationColor, brandColor } = useSalesForm();
const { setActions, clearActions } = useAppBarActions();

// Confirm dialog
const { confirm } = useConfirmDialog();

const matrixData = ref(null);
const loading = ref(false);
const error = ref("");
const exporting = ref(false);

// Track expanded/collapsed state
const expandedCities = reactive({});
const expandedLocations = reactive({});
const expandedBrands = reactive({});

// Expand all rows function
const expandAll = () => {
  if (!matrixData.value) return;

  getCitiesWithData().forEach((city) => {
    expandedCities[city] = true;

    getLocationsWithData(city).forEach((location) => {
      const locationKey = `${city}-${location}`;
      expandedLocations[locationKey] = true;

      getBrandsWithData(city, location).forEach((brand) => {
        const brandKey = `${city}-${location}-${brand}`;
        expandedBrands[brandKey] = true;
      });
    });
  });
};

// Collapse all rows function
const collapseAll = () => {
  // Clear all expanded states
  Object.keys(expandedCities).forEach((key) => delete expandedCities[key]);
  Object.keys(expandedLocations).forEach((key) => delete expandedLocations[key]);
  Object.keys(expandedBrands).forEach((key) => delete expandedBrands[key]);
};

// Computed properties for safe data checking
const hasData = computed(() => {
  return (
    matrixData.value &&
    safeArray(matrixData.value.cities).length > 0 &&
    safeArray(matrixData.value.months).length > 0
  );
});

const hasSummaryData = computed(() => {
  return (
    matrixData.value &&
    (getPlatformsWithAnyData().length > 0 || getLocationsWithAnyData().length > 0)
  );
});

// Safe data access utilities
const safeArray = (arr) => {
  return Array.isArray(arr) ? arr : [];
};

const getSafeValue = (obj, path, defaultValue = 0) => {
  if (!obj) return defaultValue;
  return path.reduce((current, key) => {
    return current && current[key] !== undefined ? current[key] : defaultValue;
  }, obj);
};

const getColspan = () => {
  if (!matrixData.value || !matrixData.value.months) return 2;
  return matrixData.value.months.length + 2;
};

// Expected combinations lookup from business rules
const expectedLookup = computed(() => {
  const combos = matrixData.value?.expectedCombinations || [];
  return {
    full: new Set(combos),
    cities: new Set(combos.map(c => c.split('|')[0])),
    cityLocations: new Set(combos.map(c => c.split('|').slice(0, 2).join('|'))),
    cityLocationBrands: new Set(combos.map(c => c.split('|').slice(0, 3).join('|'))),
  };
});

// Data filtering functions - show items with actual sales or expected by business rules
const getCitiesWithData = () => {
  if (!matrixData.value?.cities) return [];
  return safeArray(matrixData.value.cities).filter(
    (city) => getCityTotal(city) > 0 || expectedLookup.value.cities.has(city),
  );
};

const getLocationsWithData = (city) => {
  if (!matrixData.value?.locations) return [];
  return safeArray(matrixData.value.locations).filter(
    (location) => getLocationCityTotal(city, location) > 0 || expectedLookup.value.cityLocations.has(`${city}|${location}`),
  );
};

const getBrandsWithData = (city, location) => {
  if (!matrixData.value?.brands) return [];
  return safeArray(matrixData.value.brands).filter(
    (brand) => getBrandLocationCityTotal(city, location, brand) > 0 || expectedLookup.value.cityLocationBrands.has(`${city}|${location}|${brand}`),
  );
};

const getPlatformsWithData = (city, location, brand) => {
  if (!matrixData.value?.platforms) return [];
  return safeArray(matrixData.value.platforms).filter(
    (platform) => getPlatformTotalForBrand(city, location, brand, platform) > 0 || expectedLookup.value.full.has(`${city}|${location}|${brand}|${platform}`),
  );
};

const getPlatformsWithAnyData = () => {
  if (!matrixData.value?.platforms) return [];
  return safeArray(matrixData.value.platforms).filter((platform) => getPlatformTotal(platform) > 0);
};

const getLocationsWithAnyData = () => {
  if (!matrixData.value?.locations) return [];
  return safeArray(matrixData.value.locations).filter((location) => getLocationTotal(location) > 0);
};

// Expanded state functions
const isCityExpanded = (city) => {
  return expandedCities[city] === true;
};

const isLocationExpanded = (city, location) => {
  return expandedLocations[`${city}-${location}`] === true;
};

const isBrandExpanded = (city, location, brand) => {
  return expandedBrands[`${city}-${location}-${brand}`] === true;
};

const toggleCity = (city) => {
  expandedCities[city] = !expandedCities[city];
};

const toggleLocation = (city, location) => {
  const key = `${city}-${location}`;
  expandedLocations[key] = !expandedLocations[key];
};

const toggleBrand = (city, location, brand) => {
  const key = `${city}-${location}-${brand}`;
  expandedBrands[key] = !expandedBrands[key];
};

const formatMonth = (monthString) => {
  const [year, month] = monthString.split("-");
  const date = new Date(year, month - 1);
  return date.toLocaleDateString("en-US", { year: "numeric", month: "short" });
};

const formatAmount = (amount) => {
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

// Data access functions
const getLocationCityMonthTotal = (city, location, month) => {
  if (!matrixData.value?.data) return 0;
  let total = 0;
  safeArray(matrixData.value.brands).forEach((brand) => {
    safeArray(matrixData.value.platforms).forEach((platform) => {
      total += getSafeValue(matrixData.value.data, [city, location, brand, platform, month], 0);
    });
  });
  return total;
};

const getLocationCityTotal = (city, location) => {
  if (!matrixData.value?.data) return 0;
  let total = 0;
  safeArray(matrixData.value.brands).forEach((brand) => {
    safeArray(matrixData.value.platforms).forEach((platform) => {
      safeArray(matrixData.value.months).forEach((month) => {
        total += getSafeValue(matrixData.value.data, [city, location, brand, platform, month], 0);
      });
    });
  });
  return total;
};

const getBrandLocationCityMonthTotal = (city, location, brand, month) => {
  if (!matrixData.value?.data) return 0;
  let total = 0;
  safeArray(matrixData.value.platforms).forEach((platform) => {
    total += getSafeValue(matrixData.value.data, [city, location, brand, platform, month], 0);
  });
  return total;
};

const getBrandLocationCityTotal = (city, location, brand) => {
  if (!matrixData.value?.data) return 0;
  let total = 0;
  safeArray(matrixData.value.platforms).forEach((platform) => {
    safeArray(matrixData.value.months).forEach((month) => {
      total += getSafeValue(matrixData.value.data, [city, location, brand, platform, month], 0);
    });
  });
  return total;
};

const getPlatformData = (city, location, brand, platform, month) => {
  return getSafeValue(matrixData.value.data, [city, location, brand, platform, month], 0);
};

const getPlatformTotalForBrand = (city, location, brand, platform) => {
  if (!matrixData.value?.data) return 0;
  let total = 0;
  safeArray(matrixData.value.months).forEach((month) => {
    total += getSafeValue(matrixData.value.data, [city, location, brand, platform, month], 0);
  });
  return total;
};

const getCityTotal = (city) => {
  const rowTotals = getSafeValue(matrixData.value, ["rowTotals", city], {});
  return Object.values(rowTotals).reduce((sum, amount) => sum + amount, 0);
};

const getGrandMonthTotal = (month) => {
  if (!matrixData.value?.columnTotals) return 0;
  let total = 0;
  safeArray(matrixData.value.brands).forEach((brand) => {
    total += getSafeValue(matrixData.value.columnTotals, [brand, month], 0);
  });
  return total;
};

const getPlatformTotal = (platform) => {
  if (!matrixData.value?.data) return 0;
  let total = 0;
  safeArray(matrixData.value.cities).forEach((city) => {
    safeArray(matrixData.value.locations).forEach((location) => {
      safeArray(matrixData.value.brands).forEach((brand) => {
        safeArray(matrixData.value.months).forEach((month) => {
          total += getSafeValue(matrixData.value.data, [city, location, brand, platform, month], 0);
        });
      });
    });
  });
  return total;
};

const getLocationTotal = (location) => {
  if (!matrixData.value?.data) return 0;
  let total = 0;
  safeArray(matrixData.value.cities).forEach((city) => {
    safeArray(matrixData.value.brands).forEach((brand) => {
      safeArray(matrixData.value.platforms).forEach((platform) => {
        safeArray(matrixData.value.months).forEach((month) => {
          total += getSafeValue(matrixData.value.data, [city, location, brand, platform, month], 0);
        });
      });
    });
  });
  return total;
};

// Export functions with CSV - FLAT STRUCTURE
const exportToCSV = async () => {
  const dataToExport = flattenMonthlyData();

  if (!dataToExport || dataToExport.length === 0) {
    error.value = "No data available to export";
    return;
  }

  const monthsCount = matrixData.value?.months?.length || 0;
  const grandTotal = getSafeValue(matrixData.value, ["grandTotal"], 0);

  const confirmed = await confirm({
    title: "Export to CSV",
    type: "info",
    htmlMessage: `
      <p>The following data will be exported:</p>
      <ul class="mt-2">
        <li><strong>${dataToExport.length}</strong> rows</li>
        <li><strong>${monthsCount}</strong> months</li>
        <li><strong>${formatAmount(grandTotal)}</strong> AED total</li>
      </ul>
    `,
    confirmText: "Export",
    cancelText: "Cancel",
  });

  if (!confirmed) return;

  exporting.value = true;
  try {
    generateCSVFile(dataToExport);
  } catch (err) {
    console.error("Export failed:", err);
    error.value = "Failed to export data";
  } finally {
    exporting.value = false;
  }
};

const flattenMonthlyData = () => {
  if (!matrixData.value) return [];

  const flattenedData = [];
  const months = safeArray(matrixData.value.months);

  // Add header row
  // flattenedData.push({
  //   city: "City",
  //   location: "Location",
  //   brand: "Brand",
  //   platform: "Platform",
  //   ...Object.fromEntries(months.map((month) => [month, formatMonthForCSV(month)])),
  //   total: "Total",
  // });

  const cities = getCitiesWithData();

  cities.forEach((city) => {
    const locations = getLocationsWithData(city);

    // If no locations, add city total
    if (locations.length === 0) {
      flattenedData.push({
        city: city,
        location: "",
        brand: "",
        platform: "",
        ...Object.fromEntries(
          months.map((month) => [
            month,
            getSafeValue(matrixData.value.rowTotals, [city, month], 0),
          ]),
        ),
        total: getCityTotal(city),
      });
    } else {
      locations.forEach((location) => {
        const brands = getBrandsWithData(city, location);

        // If no brands, add location total
        if (brands.length === 0) {
          flattenedData.push({
            city: city,
            location: location,
            brand: "",
            platform: "",
            ...Object.fromEntries(
              months.map((month) => [month, getLocationCityMonthTotal(city, location, month)]),
            ),
            total: getLocationCityTotal(city, location),
          });
        } else {
          brands.forEach((brand) => {
            const platforms = getPlatformsWithData(city, location, brand);

            // If no platforms, add brand total
            if (platforms.length === 0) {
              flattenedData.push({
                city: city,
                location: location,
                brand: brand,
                platform: "",
                ...Object.fromEntries(
                  months.map((month) => [
                    month,
                    getBrandLocationCityMonthTotal(city, location, brand, month),
                  ]),
                ),
                total: getBrandLocationCityTotal(city, location, brand),
              });
            } else {
              platforms.forEach((platform) => {
                // Add platform detail row
                flattenedData.push({
                  city: city,
                  location: location,
                  brand: brand,
                  platform: platform,
                  ...Object.fromEntries(
                    months.map((month) => [
                      month,
                      getPlatformData(city, location, brand, platform, month),
                    ]),
                  ),
                  total: getPlatformTotalForBrand(city, location, brand, platform),
                });
              });
            }
          });
        }
      });
    }
  });

  return flattenedData;
};

const generateCSVFile = (data) => {
  if (!data.length) return;

  const months = safeArray(matrixData.value.months);

  // Define headers - FLAT STRUCTURE
  const headers = [
    "City",
    "Location",
    "Brand",
    "Platform",
    ...months.map((month) => formatMonthForCSV(month)),
    "Total",
  ];

  // Convert data to CSV rows
  const csvRows = [
    headers.join(","),
    ...data.map((item) =>
      [
        escapeCSV(item.city),
        escapeCSV(item.location),
        escapeCSV(item.brand),
        escapeCSV(item.platform),
        ...months.map((month) => formatAmountForCSV(item[month] || 0)),
        formatAmountForCSV(item.total),
      ].join(","),
    ),
  ];

  // Create CSV content
  const csvContent = csvRows.join("\n");

  // Create and download file
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  link.setAttribute("href", url);
  link.setAttribute("download", `monthly_sales_${new Date().toISOString().slice(0, 10)}.csv`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const escapeCSV = (value) => {
  if (value === null || value === undefined) return '""';
  const stringValue = String(value);
  // Escape quotes and wrap in quotes if contains comma, quote, or newline
  if (stringValue.includes('"') || stringValue.includes(",") || stringValue.includes("\n")) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }
  return stringValue;
};

const formatMonthForCSV = (monthString) => {
  const [year, month] = monthString.split("-");
  const date = new Date(year, month - 1);
  // Remove commas from the date format
  return date.toLocaleDateString("en-US", { year: "numeric", month: "long" }).replace(/,/g, "");
};

const formatAmountForCSV = (amount) => {
  // Use system locale for proper decimal formatting
  const number = typeof amount === "number" ? amount : parseFloat(amount) || 0;
  return new Intl.NumberFormat(navigator.language, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
    useGrouping: false, // Don't use thousand separators
  }).format(number);
};

const refreshData = () => {
  fetchMatrixData();
};

const fetchMatrixData = async () => {
  loading.value = true;
  error.value = "";

  try {
    const response = await api.accountingApi.get("/api/accounting/sales/monthly");
    matrixData.value = response.data;

    // Reset expanded states
    Object.keys(expandedCities).forEach((key) => delete expandedCities[key]);
    Object.keys(expandedLocations).forEach((key) => delete expandedLocations[key]);
    Object.keys(expandedBrands).forEach((key) => delete expandedBrands[key]);

    // Auto-expand cities with data for better UX
    getCitiesWithData().forEach((city) => {
      expandedCities[city] = true;
      getLocationsWithData(city).forEach((location) => {
        expandedLocations[`${city}-${location}`] = true;
        getBrandsWithData(city, location).forEach((brand) => {
          expandedBrands[`${city}-${location}-${brand}`] = true;
        });
      });
    });
  } catch (err) {
    console.error("Failed to load sales matrix:", err);
    error.value = err.response?.data?.error || "Failed to load sales matrix";
  } finally {
    loading.value = false;
  }
};

onMounted(() => {
  fetchMatrixData();
});

// App bar actions
watchEffect(() => {
  setActions([
    {
      icon: "mdi-file-delimited-outline",
      tooltip: "Export CSV",
      onClick: exportToCSV,
      loading: exporting.value,
    },
    {
      icon: "mdi-chart-bar",
      tooltip: "Chart",
      onClick: () => router.push("/admin/charts/sales/monthly"),
    },
  ]);
});
onBeforeUnmount(() => clearActions());
</script>

<style scoped>
/* Keep the same styles as before */
.sticky-table :deep(.v-table__wrapper) {
  overflow-x: auto;
  background: rgb(var(--v-theme-background));
}

.sticky-table :deep(thead tr th:first-child),
.sticky-table :deep(tbody tr td:first-child),
.sticky-table :deep(tfoot tr td:first-child) {
  position: sticky;
  left: 0;
  z-index: 2;
  background: rgb(var(--v-theme-background));
  min-width: 175px !important;
  width: 175px !important;
}

.sticky-table :deep(thead tr th:first-child) {
  z-index: 3;
}

.sticky-table :deep(tfoot tr td:first-child) {
  z-index: 3;
}

.sticky-table :deep(.city-header td:first-child .v-icon) {
  margin-left: 8px !important;
}

.sticky-table :deep(.city-header td:first-child strong) {
  margin-left: 4px !important;
}

.sticky-table :deep(.location-total-row td:first-child .v-icon) {
  margin-left: 24px !important;
}

.sticky-table :deep(.location-total-row td:first-child strong) {
  margin-left: 4px !important;
}

.sticky-table :deep(.brand-total-row td:first-child .v-icon) {
  margin-left: 40px !important;
}

.sticky-table :deep(.brand-total-row td:first-child strong) {
  margin-left: 4px !important;
}

.sticky-table :deep(.platform-row td:first-child .platform-indent) {
  margin-left: 46px !important;
}

.summary-card {
  transition: all 0.3s ease;
}

.summary-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.platform-summary,
.location-summary {
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
}

.platform-summary:last-child,
.location-summary:last-child {
  border-bottom: none;
}
</style>
